import PropTypes from "prop-types";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import RuvixxDate from "components/RuvixxDate";
import EventDatasetValue from "routes/meta_datasets/swoogo_events/EventDatasetValue";
import useCanSetEventDataset from "routes/meta_datasets/swoogo_events/useCanSetEventDataset";
import DescriptionCard from "routes/swoogo/DescriptionCard";
import {
  DetailGrid,
  DetailRow,
  OverviewSkeleton,
} from "routes/swoogo/overviewPrimitives";

// Swoogo stores the public URL without a scheme, so a bare href resolves
// relative to the app; prepend https:// so the link opens the external page.
const toHref = url => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

const EventOverview = ({ event, isLoading }) => {
  const canSetDataset = useCanSetEventDataset();

  if (isLoading || !event) {
    return <OverviewSkeleton />;
  }

  return (
    <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Event</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailGrid>
            <DetailRow label="Name">{event.name}</DetailRow>
            <DetailRow label="Status">
              {event.status ? (
                <Badge variant="secondary">{event.status}</Badge>
              ) : null}
            </DetailRow>
            <DetailRow label="Folder">{event.folder_path}</DetailRow>
            {canSetDataset ? (
              <DetailRow label="Meta Dataset">
                <EventDatasetValue eventId={event.id} />
              </DetailRow>
            ) : null}
            <DetailRow label="Public URL">
              {event.url ? (
                <a
                  href={toHref(event.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw:break-all tw:text-[var(--color-blue)]! tw:hover:underline"
                >
                  {event.url}
                </a>
              ) : null}
            </DetailRow>
            <DetailRow label="Start Date">{event.start_date}</DetailRow>
            <DetailRow label="Start Time">{event.start_time}</DetailRow>
            <DetailRow label="End Date">{event.end_date}</DetailRow>
            <DetailRow label="End Time">{event.end_time}</DetailRow>
            <DetailRow label="Last Synced">
              {event.last_synced_at ? (
                <RuvixxDate date={event.last_synced_at} />
              ) : null}
            </DetailRow>
          </DetailGrid>
        </CardContent>
      </Card>

      {event.description ? (
        <DescriptionCard description={event.description} />
      ) : null}
    </div>
  );
};

EventOverview.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    status: PropTypes.string,
    folder_path: PropTypes.string,
    url: PropTypes.string,
    start_date: PropTypes.string,
    start_time: PropTypes.string,
    end_date: PropTypes.string,
    end_time: PropTypes.string,
    last_synced_at: PropTypes.string,
    description: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
};

export default EventOverview;
